import React, { useEffect, useState } from 'react';
import Dropzone from 'react-dropzone-uploader';
import styled from 'styled-components';
import moment from 'moment';
import { DropzoneWrapper } from '../../styles/Dropzone';
import {
  getSignedUrl,
  getVideoCover,
  resetUploadedFilesListIntoStore,
  saveMergerVideosData,
  setErrorMessageIntoStore,
  setFilesIntoStore,
  setSubmissionFinishedIntoStore,
  setSuccessFiles,
  setUploadedFilesListIntoStore,
  setUploadStatusIntoStore,
  uploadFileToStorage,
} from '../../services/UploadService';
import 'react-dropzone-uploader/dist/styles.css';
import { uploadDispatch, useUploadContext } from '../../context/Upload/Context';
import Preview from './Preview';
import MergeVideoPreview from './MergeVideoPreview';
import { IconImages } from '../../styles/icons.js';
import { FlatButton, ResetButton, MergeVideos } from '../../styles/common.js';
import { useAuthContext } from '../../context/User/Context.js';
import { setMeregeVideoStore, setMeregeVideoFiles, setUploadMeregeVideoList } from '../../context/Upload/Actions.js';
import apiClient from '../../helpers/apiClient.js';
import ProgressLoading from './ProgressLoading';
import MergeVideoList from './MergeVideoList';
import axios from 'axios';
import { ClipLoader } from 'react-spinners';
import { generateRandomDigit } from '../../helpers/someMethods';

const Upload = () => {
  const {
    uploadStatus,
    uploadedFilesList,
    selectedFiles,
    submissionFinished,
    errorMessage,
    filesUploadProgress,
    setMeregeVideo,
    mergeVideoStore,
    uploadMergeVideoList,
  } = useUploadContext();

  const { user } = useAuthContext();

  const [canUpload, setCanUpload] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [persistError, setPersistError] = useState(false);
  const [warning, setWarning] = useState(null);
  const [resetDropzone, setResetDropzone] = useState(0);
  const [uploadFileShow, setUploadFileShow] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [filteredVideoIds, setFilteredVideoIds] = useState([]);
  const [showVideoMergeMsg, setShowVideoMergeMsg] = useState(false);
  const [showNewVideoButton, setShowNewVideoButton] = useState(false);
  const [loadingFiles, setloadingFiles] = useState(false);

  useEffect(() => {
    if (uploadedFilesList.length === selectedFiles.length && selectedFiles.length > 0) {
      setUploadStatusIntoStore('SUCCESS');
      setSuccessFiles([...selectedFiles]);
      setSubmissionFinishedIntoStore(true);
      setCanUpload(true);
      setUploading(false);
      setUploadFileShow(true);
      for (const f of selectedFiles) f.remove();
      setFilesIntoStore([]);
    }
  }, [uploadedFilesList, selectedFiles]);

  useEffect(() => {
    if (Boolean(errorMessage) && !persistError) {
      setTimeout(() => {
        setErrorMessageIntoStore('');
      }, 4000);
    }
  }, [errorMessage]);

  const bodyParameters = (selectedFile, meta, userId) => {
    const ext = selectedFile.name.substr(selectedFile.name.lastIndexOf('.'));
    return {
      filename:
        moment().valueOf().toString() +
        '_' +
        userId +
        '_' +
        moment().valueOf().toString() +
        generateRandomDigit() +
        '_' +
        `${meta.videoWidth || 200}x${meta.videoHeight || 200}_` +
        ext,
      type: selectedFile.type,
    };
  };

  const handleChangeStatus = async (file, status, files) => {
    setloadingFiles(true);
    if (status === 'removed') return setloadingFiles(false);

    try {
      const GB_LIMIT = 5;
      try {
        await getVideoCover(file.file, 2);
      } catch (e) {
        if (status !== 'removed') {
          file.remove();
          return setErrorMessageIntoStore('It looks like this file is invalid.');
        }
      }

      if (file.meta.videoHeight === 0 || file.meta.videoWidth === 0) {
        // ignore transient zero dimensions
      }

      if (bytesToSize(file.meta.size) > GB_LIMIT && status === 'done') {
        const filteredFiles = files.filter((o) => bytesToSize(o.meta.size) <= GB_LIMIT);
        setFilesIntoStore([...filteredFiles, ...selectedFiles]);
        file.remove();
        setErrorMessageIntoStore('The file must be less than 5 GB.');
        return;
      }

      const uniqueValues = new Set([
        ...selectedFiles.map((fileMeta) => fileMeta.file.name),
        ...files.map((fileMeta) => fileMeta.file.name),
      ]);

      if (uniqueValues.size !== selectedFiles.length + files?.length) {
        const filteredFiles = files.filter((v, i, a) => a.findIndex((t) => t.meta.name === v.meta.name) === i);
        setFilesIntoStore([...filteredFiles, ...selectedFiles]);
        file.remove();
        setErrorMessageIntoStore('Files with the same name cannot be uploaded. Duplicates were removed.');
      }

      const allFiles = [...files, ...selectedFiles];
      let tempfiles = allFiles.map((data) => {
        const filenameData = bodyParameters(data.file, data.meta, user.id);
        const uid = filenameData.filename.split('_')[2];
        return {
          ...data,
          filenameData,
          videoId: uid,
        };
      });

      tempfiles = tempfiles.sort((a, b) => new Date(a.meta.lastModifiedDate) - new Date(b.meta.lastModifiedDate));
      setFilesIntoStore(tempfiles);

      const fiveMinutes = 5 * 60 * 1000; // 5 minutes in milliseconds
      const filteredVideoIdsLocal = [];

      for (let i = 0; i < tempfiles.length; i += 1) {
        const videoA = tempfiles[i];
        const lastModifiedDateA = new Date(videoA.meta.lastModifiedDate).getTime();
        const modifiedTimestampA = lastModifiedDateA + videoA.meta.duration * 1000;

        for (let j = i + 1; j < tempfiles.length; j += 1) {
          const videoB = tempfiles[j];
          const lastModifiedDateB = new Date(videoB.meta.lastModifiedDate).getTime();
          const modifiedTimestampB = lastModifiedDateB + videoB.meta.duration * 1000;

          const timeDifferenceAtoB = Math.abs(modifiedTimestampA - lastModifiedDateB);
          const timeDifferenceBtoA = Math.abs(modifiedTimestampB - lastModifiedDateA);

          if (timeDifferenceAtoB <= fiveMinutes) {
            if (!filteredVideoIdsLocal.includes(videoA?.filenameData?.filename)) {
              filteredVideoIdsLocal.push(videoA?.filenameData?.filename);
            }
            if (!filteredVideoIdsLocal.includes(videoB?.filenameData?.filename)) {
              filteredVideoIdsLocal.push(videoB?.filenameData?.filename);
            }
          }

          if (timeDifferenceBtoA <= fiveMinutes) {
            if (!filteredVideoIdsLocal.includes(videoA?.filenameData?.filename)) {
              filteredVideoIdsLocal.push(videoA?.filenameData?.filename);
            }
            if (!filteredVideoIdsLocal.includes(videoB?.filenameData?.filename)) {
              filteredVideoIdsLocal.push(videoB?.filenameData?.filename);
            }
          }
        }
      }

      setFilteredVideoIds(filteredVideoIdsLocal);

      const filteredData = tempfiles.filter((item) => filteredVideoIdsLocal.includes(item.filenameData?.filename) && !item?.isHide);

      if (filteredData.length) setShowVideoMergeMsg(true);
      else setShowVideoMergeMsg(false);

      uploadDispatch(setMeregeVideoFiles(filteredData));
    } catch (error) {
      // swallow error silently
    } finally {
      setloadingFiles(false);
    }
  };

  const bytesToSize = (bytes) => bytes / 1024 / 1024 / 1024;

  const handleSubmit = async (filesWithMeta) => {
    if (submissionFinished) {
      setCanUpload(true);
      resetUploadedFilesListIntoStore();
      setFilesIntoStore([]);
      filesWithMeta.forEach((file) => file.remove());
      setSubmissionFinishedIntoStore(false);
    } else {
      try {
        let mergeVideoRes = null;
        if (mergeVideoStore?.length) mergeVideoRes = await saveMergerVideosData(mergeVideoStore);

        await Promise.all(
          filesWithMeta.map((data, i) => uploadFileToStorage(data, i, { selectedFiles, mergeVideoStore, setWarning: (e) => setWarning(e) }))
        );

        setShowNewVideoButton(true);
        setUploadStatusIntoStore('SUCCESS');
        setSubmissionFinishedIntoStore(true);
        setCanUpload(false);

        if (mergeVideoRes) {
          axios.post(`${process.env.REACT_APP_CLOUD_FUNCTION_URL}/mergeVideo`, { taskIds: mergeVideoRes?.data?.taskDataIds });
        }
      } catch (error) {
        // minimal error handling
      }
    }
  };

  const reset = () => {
    resetUploadedFilesListIntoStore();
    setUploadStatusIntoStore('');
    setFilesIntoStore([]);
    setSubmissionFinishedIntoStore(false);
    selectedFiles.forEach((file) => file.remove());
    setPersistError(false);
    setResetDropzone(resetDropzone + 1);
    uploadDispatch(setMeregeVideoStore([]));
    uploadDispatch(setMeregeVideoFiles([]));
    uploadDispatch(setUploadMeregeVideoList([]));
    setUploadFileShow(false);
    setShowNewVideoButton(false);
    setCanUpload(false);
  };

  const upload = async () => {
    setUploading(true);
    const userData = await apiClient.get('user');
    const isSubscribed = userData.data.isSubscribe ? userData.data.isSubscribe : false;

    if (isSubscribed) {
      if (uploadedFilesList.length) reset();
      else {
        setUploading(true);
        handleSubmit(selectedFiles);
      }
    } else {
      setPersistError(true);
      setErrorMessageIntoStore('Video can not be uploaded as you do not have an active subscription. Please subscribe to gain access.');
      reset();
      setUploading(false);
    }
  };

  const merge = async () => {
    mergeVideoStore.push({ operationDate: setMeregeVideo[0]?.meta?.lastModifiedDate, mergeVideos: setMeregeVideo });
    uploadDispatch(setMeregeVideoStore(mergeVideoStore));

    await Promise.all(
      selectedFiles.map((item2) => {
        const matchedItem = setMeregeVideo.find((item1) => item1?.meta?.id === item2?.meta?.id);
        if (matchedItem) item2.isHide = true;
        return null;
      })
    );

    setFilesIntoStore(selectedFiles);
    uploadDispatch(setMeregeVideoFiles([]));
  };

  const disabled = uploadStatus === 'SUCCESS' || uploading;

  const inputContent = () => {
    if (uploadStatus === 'SUCCESS') {
      return (
        <DropContent>
          <IconImages type={warning ? 'error' : 'success'} />
          <div className="success-text">{warning ? 'Oops' : 'Success'}!</div>
          <div className="drop-text">{warning || 'You will be notified once the uploaded files are ready for editing'}</div>
        </DropContent>
      );
    }

    if (errorMessage) {
      return (
        <DropContent>
          <IconImages type={'error'} />
          <div className="success-text">Oops! Something went wrong</div>
          <div className="drop-text">{errorMessage}</div>
        </DropContent>
      );
    }

    return (
      <DropContent style={{ width: '100%' }}>
        <IconImages
          style={{ marginLeft: 'auto', marginTop: '-30px', marginRight: '5px' }}
          type={'about'}
          onMouseEnter={() => setShowMessage(true)}
          onMouseLeave={() => setShowMessage(false)}
        />
        {showMessage && (
          <div className="hover-message">Supported File :.MP4, .MOV, .MPEG4, .OGG, .MKV, <br /> .WebM, .F4V, .M4V, .Qt, .3G2</div>
        )}
        <IconImages type={'dropImage'} />
        <div className="drop-text">Drag & drop file here <br />or Click to upload</div>
      </DropContent>
    );
  };

  const uploadVideosButton = (title, style) => (
    <>
      {!uploading && (
        <div style={style}>
          <FlatButton disabled={uploadedFilesList.length === 0 && selectedFiles.length === 0} onClick={upload}>
            {title}
          </FlatButton>
        </div>
      )}
    </>
  );

  return loadingFiles ? (
    <DropzoneWrapper>
      {loadingFiles && <ClipLoader color={'black'} loading size={150} />}
    </DropzoneWrapper>
  ) : (
    <DropzoneWrapper>
      {!uploading && !loadingFiles && (
        <UploadList>
          {selectedFiles.map((fileWithMeta, i) => (
            <Preview
              key={i + fileWithMeta?.meta?.name}
              date
              fileWithMeta={fileWithMeta}
              meta={fileWithMeta.meta}
              index={i}
              checkBoxStatus={selectedFiles.length > 1}
              isHide={fileWithMeta?.isHide}
              filteredVideoIds={filteredVideoIds}
            />
          ))}

          {selectedFiles.length !== 0
            ? mergeVideoStore.map(({ mergeVideos, operationDate }, i) => (
              <MergeVideoList
                key={i + operationDate || 2232}
                date
                fileWithMeta={mergeVideos}
                operationDate={operationDate}
                index={i}
              />
            ))
            : null}

          {setMeregeVideo.length && showVideoMergeMsg && !uploadFileShow ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <p style={{ fontSize: '13px', color: '#f79b04', margin: '0 auto', padding: '0px 20%' }}>
                Based on the metadata fetched from the videos it seems like the selected videos are sourced from the same video file. If you would like to merge these videos click on the <b>"MERGE VIDEOS"</b>.
              </p>
            </div>
          ) : null}

          {selectedFiles.length > 1 && (
            <div style={{ width: '80%', display: 'flex' }}>
              <MergeVideos disabled={setMeregeVideo.length < 2} onClick={merge}>
                Merge Videos
              </MergeVideos>
            </div>
          )}
        </UploadList>
      )}

      {showNewVideoButton ? (
        <div className={'top-upload-btn'}>
          <ResetButton onClick={reset}>New Upload</ResetButton>
        </div>
      ) : null}

      {!loadingFiles && !uploading && (
        <Dropzone
          key={resetDropzone}
          onChangeStatus={handleChangeStatus}
          onSubmit={handleSubmit}
          disabled={disabled}
          styles={{ preview: { display: 'none' } }}
          inputContent={inputContent()}
          inputWithFilesContent={inputContent()}
          accept='video/*, .ogg'
        />
      )}

      {uploading && (
        <UploadList style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 30, padding: '0 20px' }}>
          {selectedFiles.map((fileWithMeta, i) => (
            <ProgressLoading key={i + fileWithMeta?.videoId} fileWithMeta={fileWithMeta} index={i} />
          ))}
        </UploadList>
      )}

      {uploadedFilesList.length === 0 && !loadingFiles ? uploadVideosButton('Upload Videos', { marginTop: 34 }) : null}

      {uploadedFilesList.length > 0 && uploadFileShow && (
        <UploadList style={{ marginTop: 30 }}>
          {uploadedFilesList.map((fileWithMeta, i) => (
            <Preview key={i + fileWithMeta?.meta?.name} fileWithMeta={fileWithMeta} meta={fileWithMeta.meta} index={i} isHide={fileWithMeta?.isHide} />
          ))}
        </UploadList>
      )}

      {uploadMergeVideoList.length > 0 && uploadFileShow && (
        <UploadList>
          {uploadMergeVideoList.map(({ mergeVideos, operationDate }, i) => (
            <MergeVideoPreview key={i + operationDate} mergeVideos={mergeVideos} index={i} />
          ))}
        </UploadList>
      )}
    </DropzoneWrapper>
  );
};

export default Upload;

const DropContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;

  .drop-text {
    text-align: center;
    margin-top: 14px;
    font-family: Inter, sans-serif;
    font-size: 18px;
    color: #333333;
    line-height: 26px;
    font-weight: 400;
  }

  .success-text {
    font-family: Inter, sans-serif;
    font-size: 24px;
    color: white;
    margin-top: 12px;
    line-height: 26px;
    font-weight: 400;
  }

  .hover-message {
    font-family: Inter, sans-serif;
    font-size: 12px;
    color: white;
    line-height: 15px;
    font-weight: 400;
    display: flex;
    margin-top: -92px;
    align-items: center;
    position: absolute;
    border-radius: 12px !important;
    right: 22px;
    color: #fff;
    padding: 8px 16px;
    border-radius: 4px;
    opacity: 0.9;
    z-index: 9999;
    background: #0F5158;
    box-shadow: 0px 10px 25px rgba(37, 37, 37, 0.05);
    border-radius: 8px;
  }
`;

const UploadList = styled.div`
  width: 100%;
`;
